import React, { useState } from 'react';
import {
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { uploadFile } from '../api';
import { useTranslation } from 'react-i18next';
import { useCustomerLocale } from '../context/CustomerSettingsContext';

const dropZoneStyles = {
  border: '2px dashed',
  borderColor: 'divider',
  borderRadius: 2,
  p: 6,
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  backgroundColor: 'background.paper',
  minHeight: '250px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: 'action.hover',
  },
  '&.drag-active': {
    borderColor: 'primary.main',
    bgcolor: 'action.hover',
    transform: 'scale(1.01)',
  }
};

const UploadsPage = () => {
  const { t } = useTranslation();
  const locale = useCustomerLocale();

  const [selectedType, setSelectedType] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileChange = (e) => {
    handleFiles(Array.from(e.target.files));
  };

  const handleFiles = (files) => {
    const jsonFiles = files.filter(file => file.type === 'application/json');
    if (jsonFiles.length !== files.length) {
      setAlert({
        open: true,
        message: t('uploadsPage.errors.onlyJsonAllowed'),
        severity: 'error'
      });
      return;
    }
    setSelectedFiles(prev => [...prev, ...jsonFiles]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    setLoading(true);
    try {
      await Promise.all(selectedFiles.map(file => uploadFile(selectedType, file)));
      setAlert({
        open: true,
        message: t('uploadsPage.success.uploaded'),
        severity: 'success'
      });
      setSelectedFiles([]);
      setSelectedType('');
    } catch (error) {
      setAlert({
        open: true,
        message: error.response?.data?.message || t('uploadsPage.errors.uploadFailed'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert(prev => ({ ...prev, open: false }));
  };

  return (
    <Paper className="upload-container">
      <Typography variant="h5" align="center" gutterBottom>
        {t('uploadsPage.title')}
      </Typography>
      <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
        {t('uploadsPage.subtitle')}
      </Typography>

      <FormControl fullWidth variant="outlined" className="upload-select">
        <InputLabel>{t('uploadsPage.type.label')}</InputLabel>
        <Select
          value={selectedType}
          label={t('uploadsPage.type.label')}
          onChange={handleTypeChange}
        >
          <MenuItem value="operator">{t('uploadsPage.type.options.operators')}</MenuItem>
          <MenuItem value="workcell">{t('uploadsPage.type.options.workcells')}</MenuItem>
          <MenuItem value="project">{t('uploadsPage.type.options.projects')}</MenuItem>
          <MenuItem value="timeslot">{t('uploadsPage.type.options.timeslots')}</MenuItem>
        </Select>
      </FormControl>

      <Box
        sx={{
          ...dropZoneStyles,
          ...(isDragActive && {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
            transform: 'scale(1.01)',
          }),
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2, opacity: 0.8 }} />
        <Typography variant="h6" gutterBottom>
          {t('uploadsPage.dropzone.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('uploadsPage.dropzone.or')}
        </Typography>
        <Button
          variant="outlined"
          component="label"
          size="large"
          startIcon={<CloudUploadIcon />}
        >
          {t('uploadsPage.dropzone.selectFiles')}
          <input
            type="file"
            accept=".json"
            onChange={handleFileChange}
            multiple
            style={{ display: 'none' }}
          />
        </Button>
      </Box>

      {selectedFiles.length > 0 && (
        <>
          <Divider sx={{ my: 4 }} />
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
              {t('uploadsPage.selectedFiles', { count: selectedFiles.length })}
            </Typography>
            <List disablePadding>
              {selectedFiles.map((file, index) => (
                <ListItem
                  key={index}
                  disableGutters
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveFile(index)}
                      aria-label={t('uploadsPage.actions.removeFile')}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={file.name}
                    secondary={(() => {
                      const size = file.size / 1024;
                      const formattedSize = new Intl.NumberFormat(locale, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(size);
                      return t('uploadsPage.fileSizeKb', { size: formattedSize });
                    })()}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={loading || !selectedType}
            fullWidth
            size="large"
            sx={{ mt: 2 }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>{t('uploadsPage.actions.uploading')}</span>
              </Box>
            ) : (
              t('uploadsPage.actions.upload')
            )}
          </Button>
        </>
      )}

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default UploadsPage;
